import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider } from './hooks/useAuth.jsx'
import { Navbar, Footer } from './components/Layout'
import { ProtectedRoute, PublicRoute } from './components/Auth'
import { AdminProtectedRoute } from './components/Admin'
import { ScrollProgressLine } from './components/UI/AnimatedLine'

// Views
import LandingView from './views/LandingView'
import AuthView from './views/AuthView'
import DashboardView from './views/DashboardView'

// Admin Views
import { AdminLayout, AdminDashboard, UsersListView, UserDetailView } from './views/admin'

// Layout wrapper that conditionally shows navbar/footer
function AppLayout({ children }) {
  const location = useLocation()
  const isAdminRoute = location.pathname.startsWith('/admin')

  // The marketing page brings its own header, footer, and sticky call bar, and
  // deliberately doesn't link into auth/dashboard — the funnel is the phone
  // call, not a signup. See marketing/brand.md.
  const isMarketingRoute = location.pathname === '/'

  if (isAdminRoute || isMarketingRoute) {
    return children
  }

  return (
    <div className="min-h-screen bg-dark-950 text-dark-100 flex flex-col">
      {/* Scroll Progress Indicator */}
      <ScrollProgressLine />
      
      {/* Navigation */}
      <Navbar />
      
      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>
      
      {/* Footer */}
      <Footer />
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppLayout>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingView />} />

            {/* Pricing lives on the landing page now. The old credit-pack view
                still exists but shows retired $19/$49 tiers, so nothing should
                reach it. */}
            <Route path="/pricing" element={<Navigate to="/" replace />} />
            
            {/* Auth Route - redirects if already logged in */}
            <Route
              path="/auth"
              element={
                <PublicRoute>
                  <AuthView />
                </PublicRoute>
              }
            />
            
            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardView />
                </ProtectedRoute>
              }
            />
            
            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <AdminProtectedRoute>
                  <AdminLayout />
                </AdminProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<UsersListView />} />
              <Route path="users/:userId" element={<UserDetailView />} />
            </Route>
            
            {/* Catch-all redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AppLayout>
      </Router>
    </AuthProvider>
  )
}

export default App
